/**
 * The SPA's side of `window.crew` (slice 6i §3.3): what an app's sandboxed
 * iframe may ask its parent, each method small and checked. The page has no
 * network, so these are the only way its data moves; each call is answered
 * with the person's session, through the API. A message must come from the
 * app's own frame and carry this render's token (`#t=…` in the frame's URL),
 * after Executor; anything else is dropped.
 */
import type { Opened } from "./api";

export type { Opened };

type Args = Record<string, unknown>;
export type Call = { id: number; method: string; args: Args };

/** The most a page may send in one call. */
export const MAX_ARGS = 64 * 1024;
const PATH = /^(?!.*\.\.)(?!\/)[A-Za-z0-9_\-./]{1,200}$/;

/** A message from the iframe as a call, or null for anything that isn't one, or lacks `token`. */
export function parseCall(data: unknown, token: string): Call | null {
  if (!data || typeof data !== "object") return null;
  const m = data as {
    __crew?: unknown;
    token?: unknown;
    id?: unknown;
    method?: unknown;
    args?: unknown;
  };
  if (m.__crew !== 1 || m.token !== token) return null;
  if (typeof m.id !== "number" || typeof m.method !== "string") return null;
  const args =
    m.args && typeof m.args === "object" && !Array.isArray(m.args) ? (m.args as Args) : {};
  return { id: m.id, method: m.method, args };
}

function str(args: Args, key: string): string {
  const v = args[key];
  if (typeof v !== "string") throw new Error(`${key} is required`);
  return v;
}

export type BridgeDeps = {
  opened: Opened;
  /** The API, with the member's session. */
  api: <T>(method: "GET" | "POST" | "DELETE", path: string, body?: unknown) => Promise<T>;
  /** One of the app's files, from its ticket path (same origin as the SPA). */
  fetchFile: (url: string) => Promise<Response>;
  /** Tell the iframe something changed (after a mutation), so it refetches. */
  changed: () => void;
};

/** Each method the page may call. */
export function bridgeMethods(d: BridgeDeps): Record<string, (args: Args) => Promise<unknown>> {
  const base = `/api/apps/${d.opened.app.id}`;
  return {
    info: async () => ({ app: d.opened.app, me: d.opened.me }),
    file: async (args) => {
      const path = str(args, "path");
      if (!PATH.test(path)) throw new Error("Bad path");
      const res = await d.fetchFile(`${d.opened.url}${path}`);
      if (!res.ok) throw new Error(`${path}: ${res.status}`);
      return res.text();
    },
    // The app's server: a query's output; a mutation's, then every query refetches.
    query: async (args) =>
      (
        await d.api<{ value: unknown }>("POST", `${base}/call`, {
          kind: "query",
          name: str(args, "name"),
          input: args.input ?? {},
        })
      ).value,
    mutate: async (args) => {
      const { value } = await d.api<{ value: unknown }>("POST", `${base}/call`, {
        kind: "mutation",
        name: str(args, "name"),
        input: args.input ?? {},
      });
      d.changed();
      return value;
    },
    error: async (args) =>
      (
        await d.api<{ reported: boolean }>("POST", `${base}/errors`, {
          ...args,
          version: d.opened.app.version,
        })
      ).reported,
  };
}

/** A call's answer for the iframe: its value, or why it was refused. */
export async function answerCall(
  methods: ReturnType<typeof bridgeMethods>,
  call: Call,
): Promise<{ id: number; ok: true; value: unknown } | { id: number; ok: false; error: string }> {
  const run = Object.hasOwn(methods, call.method) ? methods[call.method] : undefined;
  if (!run) return { id: call.id, ok: false, error: `No such method: ${call.method}` };
  if (JSON.stringify(call.args).length > MAX_ARGS)
    return { id: call.id, ok: false, error: "Too large" };
  try {
    return { id: call.id, ok: true, value: await run(call.args) };
  } catch (e) {
    return { id: call.id, ok: false, error: e instanceof Error ? e.message : String(e) };
  }
}
